import {AxiosResponse} from 'axios';

export type PerformanceEntry = {
  responseNr: number;
  url: string;
  method: string;
  status: number;
  requestId: string;
  timing: ServerTiming;
  sqlCount?: number;
  totalElapsedMs?: number;
};

export type ServerTimingEntry = {
  desc?: string;
  dur?: number;
  [key: string]: string | number | undefined;
};
export type ServerTiming = {[key: string]: ServerTimingEntry};

// Parse a 'Server-Timing' header from django-debug-toolbar.
function parseTiming(timing: string): ServerTiming {
  if (timing.length === 0) return {};

  const timings = timing.split(',');
  const parsed: ServerTiming = {};
  for (const timing of timings) {
    const parts = timing.split(';');
    const name = parts.shift()?.trim();
    if (!name) continue;
    parsed[name] = Object.fromEntries(
      parts
        .map((p) => {
          const eqPos = p.indexOf('=');
          if (eqPos === -1) return undefined;

          const key = p.substring(0, eqPos).trim();

          let value: string | number = p.substring(eqPos + 1);
          if (value && value.startsWith('"') && value.endsWith('"')) {
            value = value.substring(1, value.length - 1);
          }
          value = value.trim();

          if (value.match(/^[0-9]+(\.[0-9]*)?$/)) {
            value = parseFloat(value);
          }

          return [key, value];
        })
        .filter((kv) => !!kv),
    );
  }
  return parsed;
}

const sqlQueryCount = (timing: ServerTiming) => {
  if (timing?.SQLPanel_sql_time && timing?.SQLPanel_sql_time.desc) {
    const match = timing?.SQLPanel_sql_time.desc.match(/SQL ([0-9]+) quer/);
    if (match) {
      return parseInt(match[1]);
    }
  }
};

export const usePerformanceStore = defineStore('performance', () => {
  const entries = ref<PerformanceEntry[]>([]);
  const nextResponseNr = ref(1);
  const modalOpen = ref(false);

  const handleResponse = (response: AxiosResponse) => {
    if (response.headers['djdt-request-id']) {
      const timing = parseTiming(response.headers['server-timing'] ?? '');
      const totalElapsedMs = timing?.TimerPanel_total_time?.dur;
      const newEntry: PerformanceEntry = {
        responseNr: nextResponseNr.value++,
        url: response.config.url ?? 'Unknown',
        method: response.config.method ?? 'Unknown',
        status: response.status,
        requestId: response.headers['djdt-request-id'],
        timing: timing,
        sqlCount: sqlQueryCount(timing),
        totalElapsedMs: totalElapsedMs && Math.floor(totalElapsedMs),
      };
      entries.value.push(newEntry);
    }
  };

  return {entries, modalOpen, handleResponse};
});
